import * as React from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {LoadingButton} from '@mui/lab';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {ColumnDef} from '@tanstack/react-table';
import Property from 'crqlar-react-common/dist/components/Property';
import {
    SurveyFeedbackTheme,
    SurveyJourneyStage,
} from 'crqlar-react-common/dist/models/survey/surveyFeedbackTheme.model';
import {SurveyService} from 'crqlar-react-common/dist/services/survey/survey.service';
import {useReadOnly} from 'crqlar-react-common/dist/utils/hooks/users/useReadOnly';
import {enqueueSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import {useQueryClient} from 'react-query';

import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    serverTableQueryKey,
    useServerTable,
} from '../../../common/serverTable';
import type {ServerTableController} from '../../../common/serverTable';
import {SingleSelect} from '../../../common/newSelect';
import {
    SURVEY_EVALUATION_QUERY_KEY,
    SURVEY_FEEDBACK_THEMES_QUERY_KEY,
    SURVEY_THEMES_QUERY_KEY,
    SURVEY_THEMES_TABLE_ID,
} from '../../shared/surveyQueryRefresh';
import {SURVEY_JOURNEY_STAGES, surveyJourneyStageLabelKey} from '../data/surveyThemeStages.util';

const THEME_PAGE_SIZE = 999;
const SCREEN_HEIGHT = 480;

const MANAGER_TITLE_ID = 'themes-manager-title';

interface ThemeDraft {
    id?: string;
    name: string;
    description: string;
    stage: SurveyJourneyStage;
}

const draftOf = (theme: SurveyFeedbackTheme): ThemeDraft => ({
    id: theme.id,
    name: theme.name ?? '',
    description: theme.description ?? '',
    stage: theme.stage ?? SurveyJourneyStage.GENERAL,
});

const newDraft = (): ThemeDraft => ({
    name: '',
    description: '',
    stage: SurveyJourneyStage.GENERAL,
});

const snack = (message: string, variant: 'success' | 'error') =>
    enqueueSnackbar(message, {variant, anchorOrigin: {vertical: 'bottom', horizontal: 'center'}});

interface ManagerHeaderProps {
    title: string;
    onBack?: () => void;
    onClose: () => void;
    disabled?: boolean;
}

function ManagerHeader({title, onBack, onClose, disabled}: ManagerHeaderProps) {
    const {t} = useTranslation();

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, minHeight: 56}}>
            {onBack != null && (
                <IconButton aria-label={t('Back')} onClick={onBack} disabled={disabled}>
                    <ArrowBackIcon/>
                </IconButton>
            )}

            <Typography
                id={MANAGER_TITLE_ID}
                component={'h2'}
                variant={'h6'}
                noWrap
                sx={{pl: onBack == null ? 1 : 0}}
            >
                {title}
            </Typography>

            <IconButton aria-label={t('Close')} onClick={onClose} disabled={disabled} sx={{ml: 'auto'}}>
                <CloseIcon/>
            </IconButton>
        </Box>
    );
}

interface ThemeRecordScreenProps {
    draft: ThemeDraft;
    onChange: (draft: ThemeDraft) => void;
    onBack: () => void;
    onClose: () => void;
    onSave: () => void;
    onDelete: () => void;
    saving: boolean;
    readOnly: boolean;
}

function ThemeRecordScreen({
    draft,
    onChange,
    onBack,
    onClose,
    onSave,
    onDelete,
    saving,
    readOnly,
}: ThemeRecordScreenProps) {
    const {t} = useTranslation();

    const isNew = draft.id == null;

    return (
        <React.Fragment>
            <ManagerHeader
                title={isNew ? t('New theme') : draft.name.trim() || t('Edit theme')}
                onBack={onBack}
                onClose={onClose}
                disabled={saving}
            />

            <DialogContent dividers sx={{p: 3, minHeight: SCREEN_HEIGHT}}>
                <Stack gap={1}>
                    <Property title={t('Name')}>
                        <TextField
                            id={'survey-theme-name'}
                            size={'small'}
                            value={draft.name}
                            onChange={event => onChange({...draft, name: event.target.value})}
                            disabled={saving}
                            fullWidth
                            autoFocus
                        />
                    </Property>

                    <Property
                        title={t('What this theme covers')}
                        tooltip={t('Replies are matched against this description, so write it the way a guest would.')}
                        labelAlignment={'top'}
                    >
                        <TextField
                            id={'survey-theme-description'}
                            size={'small'}
                            value={draft.description}
                            onChange={event => onChange({...draft, description: event.target.value})}
                            disabled={saving}
                            fullWidth
                            multiline
                            minRows={4}
                        />
                    </Property>

                    <Property title={t('Category')}>
                        <SingleSelect
                            placeholder={t('Category')}
                            value={draft.stage}
                            options={SURVEY_JOURNEY_STAGES.map(stage => ({value: stage, label: t(surveyJourneyStageLabelKey(stage))}))}
                            onChange={stage => onChange({...draft, stage: stage as SurveyJourneyStage})}
                            disabled={saving}
                            disableClearable
                        />
                    </Property>

                </Stack>
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                {!isNew && !readOnly && (
                    <Button color={'error'} onClick={onDelete} disabled={saving} sx={{mr: 'auto'}}>
                        {t('Delete')}
                    </Button>
                )}

                <Button color={'inherit'} onClick={onBack} disabled={saving}>
                    {t('Cancel')}
                </Button>

                <LoadingButton
                    variant={'contained'}
                    onClick={onSave}
                    loading={saving}
                    disabled={readOnly || draft.name.trim().length === 0}
                >
                    {isNew ? t('Create theme') : t('Save changes')}
                </LoadingButton>
            </DialogActions>
        </React.Fragment>
    );
}

interface ThemeIndexScreenProps {
    controller: ServerTableController<SurveyFeedbackTheme>;
    onCreate: () => void;
    onClose: () => void;
    readOnly: boolean;
}

function ThemeIndexScreen({controller, onCreate, onClose, readOnly}: ThemeIndexScreenProps) {
    const {t} = useTranslation();

    return (
        <React.Fragment>
            <ManagerHeader title={t('Manage themes')} onClose={onClose}/>

            <DialogContent dividers sx={{p: 2, height: SCREEN_HEIGHT}}>
                <ServerTableProvider controller={controller}>
                    <ServerTableFrame>
                        <ServerTable showPagination={false}/>
                    </ServerTableFrame>
                </ServerTableProvider>
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button color={'inherit'} onClick={onClose} sx={{ml: 'auto'}}>
                    {t('Close')}
                </Button>

                {!readOnly && (
                    <Button variant={'contained'} startIcon={<AddIcon/>} onClick={onCreate}>
                        {t('New theme')}
                    </Button>
                )}
            </DialogActions>
        </React.Fragment>
    );
}

interface DeleteThemeDialogProps {
    themeName: string;
    onCancel: () => void;
    onConfirm: () => void;
    saving: boolean;
}

function DeleteThemeDialog({themeName, onCancel, onConfirm, saving}: DeleteThemeDialogProps) {
    const {t} = useTranslation();

    return (
        <Dialog open onClose={() => !saving && onCancel()} fullWidth maxWidth={'sm'}>
            <DialogTitle>{t('Delete theme?')}</DialogTitle>

            <DialogContent>
                <Alert severity={'warning'}>
                    {t('{{name}} stops being matched against new replies and disappears from every theme filter. Replies that already mention it keep what they were labelled.', {name: themeName})}
                </Alert>
            </DialogContent>

            <DialogActions>
                <Button color={'inherit'} onClick={onCancel} disabled={saving}>
                    {t('Cancel')}
                </Button>

                <LoadingButton color={'error'} variant={'contained'} onClick={onConfirm} loading={saving}>
                    {t('Delete')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

interface ThemesManagerDialogProps {
    open: boolean;
    onClose: () => void;
}

export function ThemesManagerDialog({open, onClose}: ThemesManagerDialogProps) {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const readOnly = useReadOnly();

    const [draft, setDraft] = React.useState<ThemeDraft | null>(null);
    const [confirmDelete, setConfirmDelete] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    const columns = React.useMemo<ColumnDef<SurveyFeedbackTheme>[]>(() => [
        {
            id: 'name',
            header: t('Theme'),
            accessorFn: theme => theme.name,
            cell: ({row}) => (
                <Typography variant={'body1'} noWrap>
                    {row.original.name || row.original.id}
                </Typography>
            ),
            size: 320,
            meta: {grow: true, isServerSortable: true}
        },
        {
            id: 'stage',
            header: t('Category'),
            accessorFn: theme => theme.stage,
            cell: ({row}) => (
                <Typography variant={'body1'} color={'text.secondary'} noWrap>
                    {t(surveyJourneyStageLabelKey(row.original.stage))}
                </Typography>
            ),
            size: 200
        },
        {
            id: 'actions',
            header: '',
            cell: ({row}) => (
                <Stack direction={'row'} justifyContent={'flex-end'} data-no-row-click>
                    <Tooltip arrow title={readOnly ? t('View theme') : t('Edit theme')}>
                        <IconButton
                            size={'small'}
                            aria-label={`${readOnly ? t('View theme') : t('Edit theme')}: ${row.original.name ?? ''}`}
                            onClick={() => setDraft(draftOf(row.original))}
                        >
                            <EditOutlinedIcon fontSize={'small'}/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
            size: 72,
            meta: {align: 'right'}
        }
    ], [readOnly, t]);

    const controller = useServerTable<SurveyFeedbackTheme>({
        tableId: SURVEY_THEMES_TABLE_ID,
        presentation: 'embedded',
        defaultPageSize: THEME_PAGE_SIZE,
        columns,
        getRowId: theme => theme.id!,
        onRowClick: theme => setDraft(draftOf(theme)),
        initialSorting: [{id: 'name', desc: false}],
        enabled: open,
        queryFn: async ({pagination, sorting}) => {
            const page = await SurveyService.listThemes({
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });

            return {content: page.content ?? [], totalElements: page.totalElements ?? 0};
        }
    });

    const wasOpen = React.useRef(false);

    React.useEffect(() => {
        if (open && !wasOpen.current) {
            setDraft(null);
            setConfirmDelete(false);
        }

        wasOpen.current = open;
    }, [open]);

    const closeDialog = () => {
        if (saving) return;

        setDraft(null);
        setConfirmDelete(false);
        onClose();
    };

    const refresh = () =>
        Promise.all([
            queryClient.invalidateQueries(serverTableQueryKey(SURVEY_THEMES_TABLE_ID)),
            queryClient.invalidateQueries([SURVEY_FEEDBACK_THEMES_QUERY_KEY]),
            queryClient.invalidateQueries([SURVEY_THEMES_QUERY_KEY]),
            queryClient.invalidateQueries([SURVEY_EVALUATION_QUERY_KEY]),
        ]);

    const save = async () => {
        if (draft == null || draft.name.trim().length === 0) return;

        const changes = {
            name: draft.name.trim(),
            description: draft.description.trim(),
            stage: draft.stage,
        };

        setSaving(true);
        try {
            if (draft.id != null) {
                const stored = controller.query.data?.content.find(theme => theme.id === draft.id);
                await SurveyService.updateTheme({...stored, ...changes, id: draft.id});
            } else {
                await SurveyService.addTheme(changes);
            }

            await refresh();
            setDraft(null);
            snack(t('Changes saved!'), 'success');
        } catch (e) {
            console.log(e);
            snack(t('Changes could not be saved. Check all fields!'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const deleteTheme = async () => {
        if (draft?.id == null) return;

        setSaving(true);
        try {
            await SurveyService.archiveThemes({id: [draft.id]});
            await refresh();
            setConfirmDelete(false);
            setDraft(null);
            snack(t('Theme deleted'), 'success');
        } catch (e) {
            console.log(e);
            snack(t('Changes could not be saved. Check all fields!'), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <React.Fragment>
            <Dialog open={open} onClose={closeDialog} fullWidth maxWidth={'md'} aria-labelledby={MANAGER_TITLE_ID}>
                {draft != null ? (
                    <ThemeRecordScreen
                        draft={draft}
                        onChange={setDraft}
                        onBack={() => !saving && setDraft(null)}
                        onClose={closeDialog}
                        onSave={save}
                        onDelete={() => setConfirmDelete(true)}
                        saving={saving}
                        readOnly={readOnly}
                    />
                ) : (
                    <ThemeIndexScreen
                        controller={controller}
                        onCreate={() => setDraft(newDraft())}
                        onClose={closeDialog}
                        readOnly={readOnly}
                    />
                )}
            </Dialog>

            {confirmDelete && draft != null && (
                <DeleteThemeDialog
                    themeName={draft.name.trim() || t('This theme')}
                    onCancel={() => setConfirmDelete(false)}
                    onConfirm={deleteTheme}
                    saving={saving}
                />
            )}
        </React.Fragment>
    );
}
